import type { AuthService } from "@storytell/auth";

/**
 * Whether the viewer can be spoken for on the wire: auth has finished resolving,
 * and it left a bearer token behind.
 *
 * The authenticated reads that call this — in the hooks, integrations, prompt
 * and actions paths — turn on this one question. Other reads still assemble
 * their own gate inline, and some of them mean a different question —
 * registration, not-a-guest, a user id — which this does not answer.
 *
 * Both halves matter and neither is enough alone — a request sent while auth
 * is still settling treats a signed-in user as a stranger, and one sent
 * without a token comes back 401 for the caller to swallow. A guest counts,
 * because a guest session carries a token of its own.
 *
 * Whatever else a caller needs — the browser rather than a server render, a
 * project id, a row id — stays at the caller, which is the only place that
 * knows it.
 *
 * It reads whether a token exists, never the token itself: query options
 * factories call this, and one that tracked the token would re-run on every
 * refresh, refetch, and suspend the layout over an open chat (ENG-7617).
 */
export const authTokenReady = (auth: Pick<AuthService, "isReady" | "hasToken">): boolean =>
  auth.isReady() && auth.hasToken();
